/**
 * Risk Entity - RTK Query API
 * API endpoints for Risk domain using RTK Query with Django REST Framework
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../shared/api/baseQuery';
import { archiveArgs } from '../../../shared/api/archiveArgs';

/**
 * Map backend field names to the shape RiskCard and other UI components expect.
 */
const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const normalizeRisk = (risk) => ({
  ...risk,
  // Score field aliases (backend uses inherent_score, UI expects inherent_risk_score)
  inherent_risk_score: risk.inherent_score ?? risk.inherent_risk_score,
  residual_risk_score: risk.residual_score ?? risk.residual_risk_score,
  // Level field alias: backend sends 'inherent_level' as lowercase ('critical', 'high', etc.)
  // UI components expect 'inherent_risk_level' with capitalized value ('Critical', 'High', etc.)
  inherent_risk_level: risk.inherent_level
    ? capitalize(risk.inherent_level)
    : risk.inherent_risk_level,
  // Date aliases used by RiskCard footer
  created: risk.created_at ?? risk.created,
  modified: risk.updated_at ?? risk.modified,
  // NOTE: owner/department are kept as UUIDs so forms can match them.
  // Components that need human-readable names use risk.classification_name,
  // risk.owner_name, risk.department_name directly.
});

const normalizeListResponse = (response, fallbackPage = 1) => {
  const items = response?.results || response || [];
  return {
    data: Array.isArray(items) ? items.map(normalizeRisk) : items,
    total: response?.count || (Array.isArray(items) ? items.length : 0),
    page: fallbackPage,
  };
};

export const riskApi = createApi({
  reducerPath: 'riskApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Risk',
    'RiskCategory',
    'RiskTreatment',
    'RiskAssessment',
    'RiskStatistics',
    'KRI',
    'Incident',
    'KCI',
    'LossEvent',
    'RiskAppetite',
    'WorkflowTransition',
  ],
  endpoints: (builder) => ({
    /**
     * Get paginated list of risks with filters
     */
    getRisks: builder.query({
      query: ({ filters = {}, page = 1, limit = 20, ordering = '-created_at' } = {}) => {
        const params = new URLSearchParams();

        // Pagination
        params.set('page', page.toString());
        params.set('page_size', limit.toString());
        params.set('ordering', ordering);

        // Filters
        if (filters.status && filters.status !== 'all') {
          params.set('status', filters.status);
        }
        // The legacy RiskCategory filter is no longer offered (Classification
        // replaced it), so a `category` value left over in persisted filters
        // is deliberately never sent -- it would hide risks with no way to
        // clear it. `classification` is a Master Data Level-1 label.
        if (filters.classification && filters.classification !== 'all') {
          params.set('classification', filters.classification);
        }
        const levelValue = filters.level || filters.severity;
        if (levelValue && levelValue !== 'all') {
          // RSK-10: the backend stores/matches level lowercase
          // ('critical', 'high', ...); the FE filter UI works off
          // RISK_LEVELS' capitalised value ('Critical'). Lowercase it so
          // the filter actually matches instead of silently returning
          // nothing.
          params.set('level', levelValue.toLowerCase());
        }
        if (filters.search) {
          params.set('search', filters.search);
        }

        return {
          url: `/api/v1/risks/?${params.toString()}`,
        };
      },
      transformResponse: (response, meta, arg) => normalizeListResponse(response, arg?.page || 1),
      providesTags: (result) =>
        result?.data
          ? [...result.data.map(({ id }) => ({ type: 'Risk', id })), { type: 'Risk', id: 'LIST' }]
          : [{ type: 'Risk', id: 'LIST' }],
    }),

    /**
     * Get single risk by ID
     */
    getRiskById: builder.query({
      query: (id) => ({
        url: `/api/v1/risks/${id}/`,
      }),
      transformResponse: (response) => normalizeRisk(response),
      providesTags: (result, error, id) => [{ type: 'Risk', id }],
    }),

    /**
     * Create new risk
     */
    createRisk: builder.mutation({
      query: (newRisk) => ({
        url: '/api/v1/risks/',
        method: 'POST',
        body: newRisk,
      }),
      invalidatesTags: [{ type: 'Risk', id: 'LIST' }, { type: 'RiskStatistics' }],
    }),

    /**
     * Update existing risk
     */
    updateRisk: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/risks/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Risk', id },
        { type: 'Risk', id: 'LIST' },
        { type: 'RiskStatistics' },
      ],
    }),

    /**
     * Delete risk
     */
    deleteRisk: builder.mutation({
      // D24: archives; optional { id, reason } sends it in the request body.
      query: (arg) => {
        const { id, body } = archiveArgs(arg);
        return { url: `/api/v1/risks/${id}/`, method: 'DELETE', body };
      },
      invalidatesTags: [{ type: 'Risk', id: 'LIST' }, { type: 'RiskStatistics' }],
    }),

    /**
     * Get risk statistics summary
     */
    getRiskSummary: builder.query({
      query: () => ({
        url: '/api/v1/risks/summary/',
      }),
      providesTags: ['RiskStatistics'],
    }),

    /**
     * Get risk heatmap data: { "L-C": count } over ALL of the tenant's risks.
     *
     * RSK-14 / D15: `basis` is 'residual' (default) or 'inherent' and is always
     * sent explicitly so each basis gets its own cache entry.
     */
    getRiskHeatmap: builder.query({
      query: ({ basis = 'residual' } = {}) => ({
        url: '/api/v1/risks/heatmap/',
        params: { basis },
      }),
      providesTags: ['RiskStatistics'],
    }),

    getRiskAppetiteStatements: builder.query({
      query: ({ page = 1, limit = 20, status } = {}) => ({
        url: `/api/v1/risks/appetite-statements/?page=${page}&page_size=${limit}`,
        ...(status ? { params: { status } } : {}),
      }),
      transformResponse: (response) => ({
        data: response?.results || response || [],
        total: response?.count || 0,
      }),
      providesTags: [{ type: 'RiskAppetite', id: 'LIST' }],
    }),

    getActiveRiskAppetiteStatement: builder.query({
      query: () => ({
        url: '/api/v1/risks/appetite-statements/active/',
      }),
      providesTags: [{ type: 'RiskAppetite', id: 'ACTIVE' }],
    }),

    /** One statement's DETAIL record: approval_request_id/approval_status (R19). */
    getRiskAppetiteStatementById: builder.query({
      query: (id) => ({ url: `/api/v1/risks/appetite-statements/${encodeURIComponent(id)}/` }),
      providesTags: (result, error, id) => [{ type: 'RiskAppetite', id }],
    }),

    // Approval routing C2 (Task 8): a draft becomes the live statement only through
    // approval; a returned draft is resubmitted the same way.
    submitRiskAppetiteStatement: builder.mutation({
      query: ({ id, approvers }) => ({
        url: `/api/v1/risks/appetite-statements/${encodeURIComponent(id)}/submit/`,
        method: 'POST',
        body: approvers ? { approvers } : {},
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'RiskAppetite', id },
        { type: 'RiskAppetite', id: 'LIST' },
        { type: 'RiskAppetite', id: 'ACTIVE' },
      ],
    }),

    createRiskAppetiteStatement: builder.mutation({
      query: (payload) => ({
        url: '/api/v1/risks/appetite-statements/',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [
        { type: 'RiskAppetite', id: 'LIST' },
        { type: 'RiskAppetite', id: 'ACTIVE' },
      ],
    }),

    updateRiskAppetiteStatement: builder.mutation({
      query: ({ id, ...payload }) => ({
        url: `/api/v1/risks/appetite-statements/${encodeURIComponent(id)}/`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'RiskAppetite', id },
        { type: 'RiskAppetite', id: 'LIST' },
        { type: 'RiskAppetite', id: 'ACTIVE' },
      ],
    }),

    /**
     * Risk classification Level-1 labels from Master Data (the same taxonomy
     * cascade endpoint TaxonomyCascadeSelector reads). Feeds the register's
     * Classification filter; values are passed as ?classification=<label>.
     */
    getRiskClassifications: builder.query({
      query: () => ({
        url: '/api/v1/master-data/taxonomy/cascade/',
        params: { module: 'risk' },
      }),
      transformResponse: (response) => (Array.isArray(response?.values) ? response.values : []),
    }),

    /**
     * Get risk categories
     */
    getRiskCategories: builder.query({
      query: () => ({
        url: '/api/v1/risks/categories/',
      }),
      transformResponse: (response) => response.results || response || [],
      providesTags: ['RiskCategory'],
    }),

    /**
     * Create risk category
     */
    createRiskCategory: builder.mutation({
      query: (data) => ({
        url: '/api/v1/risks/categories/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['RiskCategory'],
    }),

    // =========================================================================
    // Risk Assessments
    // =========================================================================
    getRiskAssessments: builder.query({
      query: (riskId) => ({
        url: `/api/v1/risks/assessments/?risk_id=${riskId}`,
      }),
      transformResponse: (response) => response.results || response || [],
      providesTags: (result, error, riskId) => [{ type: 'RiskAssessment', id: riskId }],
    }),

    createRiskAssessment: builder.mutation({
      query: (data) => ({
        url: '/api/v1/risks/assessments/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { risk }) => [
        { type: 'RiskAssessment', id: risk },
        { type: 'Risk', id: risk },
      ],
    }),

    // =========================================================================
    // Risk Treatments
    // =========================================================================
    getRiskTreatments: builder.query({
      query: (riskId) => ({
        url: `/api/v1/risks/treatments/?risk_id=${riskId}`,
      }),
      transformResponse: (response) => response.results || response || [],
      providesTags: (result, error, riskId) => [{ type: 'RiskTreatment', id: riskId }],
    }),

    createRiskTreatment: builder.mutation({
      query: (data) => ({
        url: '/api/v1/risks/treatments/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { risk }) => [
        { type: 'RiskTreatment', id: risk },
        { type: 'Risk', id: risk },
      ],
    }),

    updateRiskTreatment: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/risks/treatments/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['RiskTreatment'],
    }),

    deleteRiskTreatment: builder.mutation({
      // D24: archives; optional { id, reason } sends it in the request body.
      query: (arg) => {
        const { id, body } = archiveArgs(arg);
        return { url: `/api/v1/risks/treatments/${id}/`, method: 'DELETE', body };
      },
      invalidatesTags: ['RiskTreatment'],
    }),

    updateRiskAssessment: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/risks/assessments/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['RiskAssessment'],
    }),

    deleteRiskAssessment: builder.mutation({
      // D24: archives; optional { id, reason } sends it in the request body.
      query: (arg) => {
        const { id, body } = archiveArgs(arg);
        return { url: `/api/v1/risks/assessments/${id}/`, method: 'DELETE', body };
      },
      invalidatesTags: ['RiskAssessment'],
    }),

    // =========================================================================
    // KRIs (Key Risk Indicators)
    // =========================================================================
    getKRIs: builder.query({
      query: ({ page = 1, limit = 20 } = {}) => ({
        url: `/api/v1/risks/kris/?page=${page}&page_size=${limit}`,
      }),
      transformResponse: (response) => ({
        data: response.results || response || [],
        total: response.count || 0,
      }),
      providesTags: ['KRI'],
    }),

    getKRIById: builder.query({
      query: (id) => ({
        url: `/api/v1/risks/kris/${id}/`,
      }),
      providesTags: (result, error, id) => [{ type: 'KRI', id }],
    }),

    createKRI: builder.mutation({
      query: (data) => ({
        url: '/api/v1/risks/kris/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['KRI'],
    }),

    updateKRI: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/risks/kris/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'KRI', id }],
    }),

    deleteKRI: builder.mutation({
      query: (id) => ({
        url: `/api/v1/risks/kris/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['KRI'],
    }),

    // KRI Readings
    getKRIReadings: builder.query({
      query: (arg) => {
        const kriId = typeof arg === 'object' ? arg?.kriId : arg;

        return {
          url: `/api/v1/risks/kri-readings/?kri_id=${kriId}`,
        };
      },
      transformResponse: (response) => response.results || response || [],
    }),

    createKRIReading: builder.mutation({
      query: (data) => ({
        url: '/api/v1/risks/kri-readings/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['KRI'],
    }),

    // =========================================================================
    // Incidents
    // =========================================================================
    getIncidents: builder.query({
      query: ({ page = 1, limit = 20, affected_unit, owning_unit } = {}) => ({
        url: '/api/v1/risks/incidents/',
        params: {
          page,
          page_size: limit,
          ...(affected_unit ? { affected_unit } : {}),
          ...(owning_unit ? { owning_unit } : {}),
        },
      }),
      transformResponse: (response, meta, arg) => normalizeListResponse(response, arg?.page || 1),
      providesTags: ['Incident'],
    }),

    // Detail record: carries has_breach_record, which the list does not.
    getIncident: builder.query({
      query: (id) => ({ url: `/api/v1/risks/incidents/${encodeURIComponent(String(id))}/` }),
      providesTags: ['Incident'],
    }),

    createIncident: builder.mutation({
      query: (data) => ({
        url: '/api/v1/risks/incidents/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Incident', { type: 'Risk', id: 'LIST' }],
    }),

    // RSK-16: the backend's IncidentViewSet already supports PATCH/DELETE
    // (plain ModelViewSet) -- these were just never wired up on the
    // frontend, so the "View" button in the incidents table had no
    // onClick and there was no edit/delete UI at all.
    updateIncident: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/risks/incidents/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['Incident'],
    }),

    deleteIncident: builder.mutation({
      // D24: archives; optional { id, reason } sends it in the request body.
      query: (arg) => {
        const { id, body } = archiveArgs(arg);
        return { url: `/api/v1/risks/incidents/${id}/`, method: 'DELETE', body };
      },
      invalidatesTags: ['Incident'],
    }),

    /**
     * Transition incident to a new workflow state.
     * Body: { to_state, comment?, evidence_ref? }
     */
    transitionIncident: builder.mutation({
      query: ({ id, to_state, comment = '', evidence_ref = '' }) => ({
        url: `/api/v1/risks/incidents/${id}/transition/`,
        method: 'POST',
        body: { to_state, comment, evidence_ref },
      }),
      invalidatesTags: (result, error, { id }) => ['Incident', { type: 'WorkflowTransition', id }],
    }),
    /**
     * Get available transitions for an incident from its current state.
     * Returns: string[] (target state names) -- same envelope-unwrap as
     * getRiskAvailableTransitions; see that query's comment for why.
     */
    getIncidentAvailableTransitions: builder.query({
      query: (id) => ({ url: `/api/v1/risks/incidents/${id}/transitions/available/` }),
      transformResponse: (response) => response?.available_transitions ?? [],
      providesTags: (result, error, id) => [{ type: 'WorkflowTransition', id }],
    }),
    /**
     * Get full transition history log for an incident.
     * Returns: [{ from_state, to_state, comment, evidence_ref, transitioned_by, transitioned_at }]
     * -- same triggered_by/triggered_at -> transitioned_by/transitioned_at
     * remap as getRiskTransitionHistory; see that query's comment for why.
     */
    getIncidentTransitionHistory: builder.query({
      query: (id) => ({ url: `/api/v1/risks/incidents/${id}/transitions/history/` }),
      transformResponse: (response) =>
        (Array.isArray(response) ? response : []).map((entry) => ({
          ...entry,
          transitioned_by: entry.transitioned_by ?? entry.triggered_by ?? null,
          transitioned_at: entry.transitioned_at ?? entry.triggered_at ?? null,
        })),
      providesTags: (result, error, id) => [{ type: 'WorkflowTransition', id }],
    }),

    // =========================================================================
    // KCIs (Key Control Indicators)
    // =========================================================================
    getKCIs: builder.query({
      query: ({ page = 1, limit = 20 } = {}) => ({
        url: `/api/v1/risks/kcis/?page=${page}&page_size=${limit}`,
      }),
      transformResponse: (response, meta, arg) => normalizeListResponse(response, arg?.page || 1),
      providesTags: ['KCI'],
    }),

    createKCI: builder.mutation({
      query: (data) => ({
        url: '/api/v1/risks/kcis/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['KCI'],
    }),

    updateKCI: builder.mutation({
      query: ({ id, ...patch }) => ({
        url: `/api/v1/risks/kcis/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'KCI', id }],
    }),

    addKCIReading: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/risks/kcis/${id}/add_reading/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['KCI'],
    }),

    // =========================================================================
    // Loss Events (Operational Risk)
    // =========================================================================
    getLossEvents: builder.query({
      query: ({ page = 1, limit = 20 } = {}) => ({
        url: `/api/v1/risks/loss-events/?page=${page}&page_size=${limit}`,
      }),
      transformResponse: (response, meta, arg) => normalizeListResponse(response, arg?.page || 1),
      providesTags: ['LossEvent'],
    }),

    createLossEvent: builder.mutation({
      query: (data) => ({
        url: '/api/v1/risks/loss-events/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['LossEvent'],
    }),

    validateLossEvent: builder.mutation({
      query: (id) => ({
        url: `/api/v1/risks/loss-events/${id}/validate/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['LossEvent'],
    }),

    // RSK-19: a loss event is created in 'draft' status and has no path to
    // 'submitted' without this -- the Validate action (gated on
    // status === 'submitted') was otherwise permanently unreachable.
    submitLossEvent: builder.mutation({
      query: (id) => ({
        url: `/api/v1/risks/loss-events/${id}/submit/`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['LossEvent'],
    }),

    // =========================================================================
    // Workflow Transitions (WorkflowEngine)
    // POST /api/v1/risks/{id}/transition/
    // GET  /api/v1/risks/{id}/transitions/available/
    // GET  /api/v1/risks/{id}/transitions/history/
    // RSK-04: the URLs below previously pointed at available_transitions/ and
    // transition_history/, which don't exist on the backend (real url_path
    // values are 'transitions/available' and 'transitions/history' --
    // see apps/shared/workflow/mixins.py) -> both queries 404'd and the
    // Workflow tab was permanently empty.
    // =========================================================================

    /**
     * Transition a risk to a new workflow state.
     * Body: { to_state, comment?, evidence_ref? }
     */
    transitionRisk: builder.mutation({
      query: ({ id, to_state, comment = '', evidence_ref = '' }) => ({
        url: `/api/v1/risks/${id}/transition/`,
        method: 'POST',
        body: { to_state, comment, evidence_ref },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Risk', id },
        { type: 'Risk', id: 'LIST' },
        { type: 'WorkflowTransition', id },
        { type: 'RiskStatistics' },
      ],
    }),

    /**
     * Get available transitions for a risk from its current state.
     *
     * RSK-05: the backend's actual response is
     * `{ current_state, available_transitions: [<state name string>, ...], workflow_key }`
     * -- plain strings, not `{ to_state, label, required_evidence }` objects.
     * WorkflowPanel expects the latter shape (it calls
     * `available.find((t) => t.to_state === selectedState)`), so this
     * unwraps the envelope and returns the raw state-name strings; the
     * display shape WorkflowPanel needs is derived separately from
     * `shared/config/workflowConfig.js`'s
     * `getAvailableTransitions(workflowKey, currentState)`, which is the
     * FE-side single source of truth for transition labels /
     * required_evidence flags and does not depend on this endpoint at all.
     * Returns: string[] (target state names)
     */
    getRiskAvailableTransitions: builder.query({
      query: (id) => ({ url: `/api/v1/risks/${id}/transitions/available/` }),
      transformResponse: (response) => response?.available_transitions ?? [],
      providesTags: (result, error, id) => [{ type: 'WorkflowTransition', id }],
    }),

    /**
     * Get full transition history log for a risk.
     * Returns: [{ from_state, to_state, comment, evidence_ref, transitioned_by, transitioned_at }]
     * (transformResponse renames the backend's actual
     * `triggered_by`/`triggered_at` fields -- see
     * apps/shared/workflow/serializers.py::WorkflowTransitionLogSerializer --
     * to the `transitioned_by`/`transitioned_at` names WorkflowPanel's
     * HistoryRow reads, so history rows aren't always blank. NEW-RSK-03.)
     */
    getRiskTransitionHistory: builder.query({
      query: (id) => ({ url: `/api/v1/risks/${id}/transitions/history/` }),
      transformResponse: (response) =>
        (Array.isArray(response) ? response : []).map((entry) => ({
          ...entry,
          transitioned_by: entry.transitioned_by ?? entry.triggered_by ?? null,
          transitioned_at: entry.transitioned_at ?? entry.triggered_at ?? null,
        })),
      providesTags: (result, error, id) => [{ type: 'WorkflowTransition', id }],
    }),
  }),
});

export const riskApiTagTypes = [
  'Risk',
  'RiskCategory',
  'RiskTreatment',
  'RiskAssessment',
  'RiskStatistics',
  'KRI',
  'Incident',
  'KCI',
  'LossEvent',
  'RiskAppetite',
  'WorkflowTransition',
];

export const {
  // Risks
  useGetRisksQuery,
  useGetRiskByIdQuery,
  useCreateRiskMutation,
  useUpdateRiskMutation,
  useDeleteRiskMutation,
  useGetRiskSummaryQuery,
  useGetRiskHeatmapQuery,
  useGetRiskAppetiteStatementsQuery,
  useGetActiveRiskAppetiteStatementQuery,
  useCreateRiskAppetiteStatementMutation,
  useUpdateRiskAppetiteStatementMutation,
  useGetRiskAppetiteStatementByIdQuery,
  useSubmitRiskAppetiteStatementMutation,
  // Classification (Master Data taxonomy Level 1)
  useGetRiskClassificationsQuery,
  // Categories
  useGetRiskCategoriesQuery,
  useCreateRiskCategoryMutation,
  // Assessments
  useGetRiskAssessmentsQuery,
  useCreateRiskAssessmentMutation,
  useUpdateRiskAssessmentMutation,
  useDeleteRiskAssessmentMutation,
  // Treatments
  useGetRiskTreatmentsQuery,
  useCreateRiskTreatmentMutation,
  useUpdateRiskTreatmentMutation,
  useDeleteRiskTreatmentMutation,
  // KRIs
  useGetKRIsQuery,
  useGetKRIByIdQuery,
  useCreateKRIMutation,
  useUpdateKRIMutation,
  useDeleteKRIMutation,
  useGetKRIReadingsQuery,
  useCreateKRIReadingMutation,
  // Incidents
  useGetIncidentsQuery,
  useGetIncidentQuery,
  useCreateIncidentMutation,
  useUpdateIncidentMutation,
  useDeleteIncidentMutation,
  useTransitionIncidentMutation,
  useGetIncidentAvailableTransitionsQuery,
  useGetIncidentTransitionHistoryQuery,
  // KCIs
  useGetKCIsQuery,
  useCreateKCIMutation,
  useUpdateKCIMutation,
  useAddKCIReadingMutation,
  // Loss Events
  useGetLossEventsQuery,
  useCreateLossEventMutation,
  useValidateLossEventMutation,
  useSubmitLossEventMutation,
  // Workflow
  useTransitionRiskMutation,
  useGetRiskAvailableTransitionsQuery,
  useGetRiskTransitionHistoryQuery,
} = riskApi;

export default riskApi;
